"use client";

import { useEffect, useRef, useState } from "react";
import { afterPageLoadAndIdle, allowsAutomaticVideo, whenVisible } from "@/lib/deferred-media";
import { PauseIcon, PlayIcon } from "./icons";

/**
 * Decorative surf clip behind the hero. The server renders the poster image
 * (the LCP element); this island mounts the <video> only after the page has
 * loaded and gone idle, and only when motion, data saver and connection allow.
 * Reduced motion starts paused; the 44px button plays or pauses it.
 */
export function HeroBackgroundVideo({ buttonClassName }: { buttonClassName: string }) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [enabled, setEnabled] = useState(false);
  const [paused, setPaused] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const element = anchorRef.current;
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    // Reduced motion: never mount the video automatically. It stays on the
    // poster (showingVideo is false) until the visitor presses play.
    if (reducedMotion) return;
    const connection = (navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string; downlink?: number };
    }).connection;
    if (!element || !allowsAutomaticVideo({
      reducedMotion,
      saveData: connection?.saveData,
      effectiveType: connection?.effectiveType,
      downlink: connection?.downlink,
    })) return;
    let stopObserving = () => {};
    const cancel = afterPageLoadAndIdle(() => {
      stopObserving = whenVisible(element, () => setEnabled(true), "0px");
    });
    return () => { cancel(); stopObserving(); };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (paused) {
      video.pause();
    } else {
      video.muted = true;
      void video.play()?.catch(() => {});
    }
  }, [paused, enabled]);

  const showingVideo = enabled && !paused;

  function toggle() {
    if (!enabled) {
      setEnabled(true);
      setPaused(false);
      return;
    }
    setPaused((value) => !value);
  }

  return (
    <>
      <span ref={anchorRef} aria-hidden="true" className="pointer-events-none absolute inset-0" />
      {enabled ? (
        <video
          ref={videoRef}
          src="/landing-surf-vid.mp4"
          muted
          loop
          playsInline
          autoPlay={!paused}
          preload="none"
          aria-hidden="true"
          tabIndex={-1}
          onPlaying={() => setPlaying(true)}
          onError={() => { setEnabled(false); setPlaying(false); }}
          className={`absolute inset-0 h-full w-full object-cover object-[46%_38%] transition-opacity duration-700 md:object-[50%_40%] ${
            playing ? "opacity-100" : "opacity-0"
          }`}
        />
      ) : null}
      <button
        type="button"
        onClick={toggle}
        aria-label={showingVideo ? "Pause background video" : "Play background video"}
        className={`z-[3] flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-navy/45 text-white backdrop-blur-md transition-colors hover:bg-navy/70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-light ${buttonClassName}`}
      >
        {showingVideo ? <PauseIcon /> : <PlayIcon />}
      </button>
    </>
  );
}
